import { RootState } from '../redux/store';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  IAgentDashboard,
  IDashBoardTiful,
  IDashboardTodayOrders,
  IGraphTiful,
  INoamDashboard,
  ITableDashboardList,
  ITifulDashboardGraph,
  ManagerDashboardData,
} from '../models/IDashBoard/IDashBoard';
import { API_BASE_URL } from '../helpers/common';

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['IntegrationDashboard'],
  endpoints: (build) => ({
    counterTifulDashboard: build.query<IDashBoardTiful, string>({
      transformResponse: (response: { data: IDashBoardTiful }) => response.data,
      query: () => ({
        url: '/tiful/dashboard',
        method: 'GET',
      }),
    }),
    pieGraphTifulDashboard: build.query<
      IGraphTiful[],
      { from_date: string; to_date: string }
    >({
      transformResponse: (response: { data: IGraphTiful[] }) => response.data,
      query: ({ from_date, to_date }) => ({
        url: '/tiful/dashboard/pie',
        method: 'GET',
        params: { from_date, to_date },
      }),
    }),
    popularProductsGraphTifulDashboard: build.query<
      IGraphTiful[],
      { from_date: string; to_date: string }
    >({
      transformResponse: (response: { data: IGraphTiful[] }) => response.data,
      query: ({ from_date, to_date }) => ({
        url: '/tiful/dashboard/pupular-products',
        method: 'GET',
        params: { from_date, to_date },
      }),
    }),
    getManagerSectionData: build.query<ManagerDashboardData, string>({
      transformResponse: (response: { data: ManagerDashboardData }) =>
        response.data,
      query: () => ({
        url: '/manager/dashboard',
        method: 'GET',
      }),
    }),
    // DISABLED: noam dashboard endpoints
    // noamDashboard: build.query<INoamDashboard, string>({
    //   transformResponse: (response: { data: INoamDashboard }) => response.data,
    //   query: () => ({
    //     url: '/noam/dashboard',
    //     method: 'GET',
    //   }),
    // }),
    // noamDashboardTotalSale: build.query<any, { type: string }>({
    //   transformResponse: (response: { data: any }) => response.data,
    //   query: ({ type }) => ({
    //     url: '/noam/dashboard/total-sale',
    //     method: 'GET',
    //     params: { type },
    //   }),
    // }),
    agentDashboardTotalSale: build.query<any, { type: string }>({
      transformResponse: (response: { data: any }) => response.data,
      query: ({ type }) => ({
        url: '/agent/dashboard/total-sale',
        method: 'GET',
        params: { type },
      }),
    }),
    // noamDashboardCountSale: build.query<any, { type: string }>({
    //   transformResponse: (response: { data: any }) => response.data,
    //   query: ({ type }) => ({
    //     url: '/noam/dashboard/count-sale',
    //     method: 'GET',
    //     params: { type },
    //   }),
    // }),
    getTifulDashboardGraph: build.query<
      ITifulDashboardGraph[],
      { type: string; from_date: string; to_date: string }
    >({
      transformResponse: (response: { data: ITifulDashboardGraph[] }) =>
        response.data,
      query: ({ type, from_date, to_date }) => ({
        url: '/tiful/dashboard/graph',
        method: 'GET',
        params: { type, from_date, to_date },
      }),
    }),
    managerDashboardGraph: build.query<
      ITifulDashboardGraph[],
      { type: string; from_date: string; to_date: string }
    >({
      transformResponse: (response: { data: ITifulDashboardGraph[] }) =>
        response.data,
      query: ({ type, from_date, to_date }) => ({
        url: 'api/manager/dashboard/graph',
        method: 'GET',
        params: { type, from_date, to_date },
      }),
    }),
    managerDashboardList: build.query<
      ITableDashboardList[],
      { type: string; from_date: string; to_date: string; agent_id: string }
    >({
      transformResponse: (response: { data: ITableDashboardList[] }) =>
        response.data,
      query: ({ type, from_date, to_date, agent_id }) => ({
        url: 'api/manager/dashboard/list',
        method: 'GET',
        params: { agent_id, type, to_date, from_date },
      }),
    }),
    managerGraphByAgent: build.query<
      any[],
      { type: string; from_date: string; to_date: string; agent_id: string }
    >({
      transformResponse: (response: { data: any[] }) => response.data,
      query: ({ type, from_date, to_date, agent_id }) => ({
        url: '/manager/dashboard/graph-by-agent',
        method: 'GET',
        params: { agent_id, type, to_date, from_date },
      }),
    }),
    agentDashboard: build.query<IAgentDashboard, any>({
      transformResponse: (response: { data: IAgentDashboard }) => response.data,
      query: () => ({
        url: '/agent/dashboard',
        method: 'GET',
      }),
    }),
    dashboardTodayOrders: build.query<
      IDashboardTodayOrders[],
      { from_date: string; to_date: string }
    >({
      transformResponse: (response: { data: IDashboardTodayOrders[] }) =>
        response.data,
      query: ({ from_date, to_date }) => ({
        url: 'api/agent/dashboard/today-orders',
        method: 'GET',
        params: { to_date, from_date },
      }),
    }),
    // Generic manager dashboard endpoint - single route URL with type parameter
    // This is the base method that all specific endpoints use
    getManagerDashboardData: build.query<any, number>({
      transformResponse: (response: { data: any }) => response.data,
      query: (type) => ({
        url: '/manager/dashboard',
        method: 'GET',
        params: { type },
      }),
    }),
    // Type 1: Location Status - uses the generic endpoint
    getLocationStatus: build.query<
      Array<{ title: string; value: number; id: string }>,
      void
    >({
      query: () => ({
        url: '/manager/dashboard',
        params: { type: 1 },
      }),
      transformResponse: (response: { data: Array<{ title: string; value: number; id: string }> }) =>
        response.data,
    }),
    // Type 2: Location by Warehouse - uses the generic endpoint
    getLocationByWarehouse: build.query<
      Array<{ los_ID: number; los_Code: string; los_Description: string; location: Array<{ status: string; count: number; percent: number }>; id: string }>,
      void
    >({
      query: () => ({
        url: '/manager/dashboard',
        params: { type: 2, limit: 5 },
      }),
      transformResponse: (response: { data: Array<{ los_ID: number; los_Code: string; los_Description: string; location: Array<{ status: string; count: number; percent: number }>; id: string }> }) =>
        response.data,
    }),
    // Type 3: Stock Owner Summary - uses the generic endpoint
    getStockOwnerSummary: build.query<
      Array<{ dep_ID: number; dep_Code: string; cmp_FullName: string; cmp_Address: string | null; cmp_City: string | null; cmp_ZipCode: string | null; cmp_Phone: string; cmp_Responsible: string; cmp_Fax: string | null; cmp_AFM: string | null; cmp_DUNSNumber: string | null; cmp_LastUpdateTime: string | null; summary: { count: number; percent: number; cumulativePercent: number }; los_ID: number; id: string }>,
      void
    >({
      query: () => ({
        url: '/manager/dashboard',
        params: { type: 3, limit: 5 },
      }),
      transformResponse: (response: { data: Array<{ dep_ID: number; dep_Code: string; cmp_FullName: string; cmp_Address: string | null; cmp_City: string | null; cmp_ZipCode: string | null; cmp_Phone: string; cmp_Responsible: string; cmp_Fax: string | null; cmp_AFM: string | null; cmp_DUNSNumber: string | null; cmp_LastUpdateTime: string | null; summary: { count: number; percent: number; cumulativePercent: number }; los_ID: number; id: string }> }) =>
        response.data,
    }),
    // Type 4: Stock Owner by Warehouse - uses the generic endpoint
    getStockOwnerByWarehouse: build.query<
      Array<{ los_ID: number; los_Code: string; los_Description: string; dep_Code: string; cmp_FullName: string; Count: number; Percent: number; CumulativePercent: number }>,
      void
    >({
      query: () => ({
        url: '/manager/dashboard',
        params: { type: 4, limit: 5 },
      }),
      transformResponse: (response: { data: Array<{ los_ID: number; los_Code: string; los_Description: string; dep_Code: string; cmp_FullName: string; Count: number; Percent: number; CumulativePercent: number }> }) =>
        response.data,
    }),
    // Integration Dashboard - backend resolves integration from token (companyId)
    getIntegrationDashboard: build.query<any, { role: string; limit?: number }>({
      transformResponse: (response: { data: any }) => response.data,
      query: ({ role, limit }) => ({
        url: `/${role}/dashboard`,
        method: 'GET',
        params: limit != null ? { limit } : undefined,
      }),
    }),
  }),
});

export const {
  useCounterTifulDashboardQuery,
  usePieGraphTifulDashboardQuery,
  usePopularProductsGraphTifulDashboardQuery,
  useGetManagerSectionDataQuery,
  useAgentDashboardTotalSaleQuery,
  useGetTifulDashboardGraphQuery,
  useManagerDashboardGraphQuery,
  useManagerDashboardListQuery,
  useManagerGraphByAgentQuery,
  useAgentDashboardQuery,
  useDashboardTodayOrdersQuery,
  useGetManagerDashboardDataQuery,
  useGetLocationStatusQuery,
  useGetLocationByWarehouseQuery,
  useGetStockOwnerSummaryQuery,
  useGetStockOwnerByWarehouseQuery,
  useGetIntegrationDashboardQuery,
} = dashboardApi;
